import { useState } from 'react';

import LoaderButton from 'components/Button/LoaderButton';
import { RegisterImplementationProps } from 'constants/types';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import { AnalyticsEventName, track } from 'utils/analytics';
import { useOAuthStatePolling } from '../../OAuth/useOAuthStatePolling';
import SignInBase from '../SignInBase';
import { getProviderFormLabel } from '../utils';

// Fallback for older backends that don't serve `scopes` in the validate
// response. Remote serves the same scopes in sandbox and production.
const REMOTE_SCOPES = [
  'employment:read',
  'payroll_run:read',
  'company:read',
  'company_department:read',
];

// Shape of provider.oauthCredentials in the validate response. The installed
// common types predate redirectUri and scopes.
export type RemoteOAuthCredentials = {
  clientId?: string | null;
  authorizeUrl?: string | null;
  redirectUri?: string | null;
  scopes?: string[] | null;
};

export const getOAuthUrl = ({
  config,
  sessionKey,
}: {
  config: RemoteOAuthCredentials | null;
  sessionKey: string;
}) => {
  if (!config?.authorizeUrl || !config?.clientId || !config?.redirectUri) {
    throw new Error('Invalid OAuth config');
  }

  // Remote's authorization endpoint takes no response_type; the authorization
  // code is the only grant it serves.
  const url = new URL(config.authorizeUrl);
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('redirect_uri', config.redirectUri);
  url.searchParams.set('scope', (config.scopes ?? REMOTE_SCOPES).join(' '));
  url.searchParams.set('state', JSON.stringify({ sessionKey }));

  return url.toString();
};

const RegisterRemoteOAuth = ({
  provider,
  onSubmit,
  setError,
  sessionKey,
  onMockOAuth,
}: RegisterImplementationProps & AuthorizeContextValue) => {
  const [buttonLoading, setButtonLoading] = useState(false);

  const config = provider.oauthCredentials as RemoteOAuthCredentials | null;

  const onCodeReceived = async (clientCode: string) => {
    await onSubmit({
      clientCode,
      // Remote matches the redirect_uri on the token exchange against the one
      // sent to authorize, so both legs use the registered value.
      providerRedirectUri: config?.redirectUri ?? undefined,
    });
  };

  const { loading, openOAuthWindow } = useOAuthStatePolling({
    sessionKey,
    onCodeReceived,
    setError,
  });

  const onOAuthClick = async () => {
    setButtonLoading(true);
    const didMockOAuth = await onMockOAuth();

    if (didMockOAuth) {
      setButtonLoading(false);
      return;
    }

    const oauthUrl = getOAuthUrl({ config, sessionKey });

    track(AnalyticsEventName.RedirectedToOAuthUrl, {
      oauthUrl,
    });

    openOAuthWindow(oauthUrl);
    setButtonLoading(false);
  };

  const instructions = (
    <p>
      Remote authentication will continue in a new window. Please make sure to
      allow popups and finish authenticating through Remote in the new window.
    </p>
  );

  return (
    <SignInBase
      formLabel={getProviderFormLabel(provider)}
      instructions={instructions}
    >
      <div
        style={{
          marginTop: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LoaderButton
          isLoading={loading || buttonLoading}
          disabled={loading || buttonLoading}
          onClick={onOAuthClick}
          text="Connect"
        />
      </div>
    </SignInBase>
  );
};

export default withAuthorizeContext(RegisterRemoteOAuth);
